import React, { useState, useEffect } from 'react';
import { UtilService } from 'app/services/util.service';
import { CheckboxControlProps } from 'app/components/Controls/CheckboxControl/CheckboxControl.interfaces';
import { FieldsEnum } from 'app/shared/enums';

// TODO change functions names in this file

export function useCheckboxControl({
  meta: { touched, valid },
  input,
  onValueChange,
  onClick,
  label = '',
  extensionPosition,
  showAllLabel,
  questionKey,
  withoutDotsAtExtensionPoint,
  checkedValue,
  uncheckedValue,
}: CheckboxControlProps) {
  const hasEnumMapping =
    checkedValue !== undefined && uncheckedValue !== undefined;
  const normalizeBooleanValue = (value: unknown): boolean => {
    if (typeof value === 'string') {
      const normalizedValue = value.trim().toLowerCase();
      if (normalizedValue === 'true') return true;
      if (normalizedValue === 'false') return false;
    }

    return Boolean(value);
  };

  const getLabel = () => {
    return label.replace(expandingPointMark, '');
  };
  const marketingConsentPrefix = 'Zgadzam się na otrzymywanie';
  const startsWithMarketingConsent = label.startsWith(marketingConsentPrefix);
  const labelLength =
    extensionPosition || (startsWithMarketingConsent ? 144 : 140);
  const isDefaultBreakLength =
    !extensionPosition && !startsWithMarketingConsent;

  const expandingPointMark = '^^';
  const error = touched && !valid;
  const currentValue = normalizeBooleanValue(input.value);
  const checked = hasEnumMapping
    ? input.value === checkedValue
    : questionKey === FieldsEnum.PersonalDataPath
    ? !currentValue
    : currentValue;

  const getLabelWithoutHTML = (innerText: string) => {
    let tmp = document.createElement('DIV');
    tmp.innerHTML = innerText;
    return tmp.textContent || tmp.innerText || '';
  };

  const getLabelLengthWithoutHTML = (innerText: string) => {
    return getLabelWithoutHTML(innerText).length;
  };

  const isWordBoundaryChar = (char: string): boolean =>
    char === ' ' || char.charCodeAt(0) === 160;

  const getSafeBreakLength = (text: string, maxLength: number): number => {
    if (text.length <= maxLength) {
      return maxLength;
    }

    const breaksMidWord =
      !isWordBoundaryChar(text[maxLength]) &&
      !isWordBoundaryChar(text[maxLength - 1]);

    if (!breaksMidWord) {
      return maxLength;
    }

    const lastSpaceIndex = Math.max(
      text.lastIndexOf(' ', maxLength),
      text.lastIndexOf(String.fromCharCode(160), maxLength)
    );

    return lastSpaceIndex > 0 ? lastSpaceIndex : maxLength;
  };

  const getLabelLength = () => {
    const strippedLabel = getLabelWithoutHTML(label);
    const breakIndex = strippedLabel.indexOf(expandingPointMark);

    if (breakIndex !== -1) {
      return breakIndex;
    }

    if (isDefaultBreakLength) {
      return getSafeBreakLength(strippedLabel, labelLength);
    }

    return labelLength;
  };

  const calculateExpanded = () => {
    const labelWithoutMark = label.replace(expandingPointMark, '');
    const labelLengthWithoutHTML = getLabelLengthWithoutHTML(labelWithoutMark);
    const labelLengthValue = getLabelLength();
    return labelLengthWithoutHTML < labelLengthValue;
  };

  const [expanded, setExpanded] = useState(calculateExpanded());

  useEffect(() => {
    const shouldBeExpanded = calculateExpanded();
    setExpanded(shouldBeExpanded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [label, extensionPosition]);

  const handleChange = () => {
    setTimeout(() => {
      const nextValue: string | boolean = hasEnumMapping
        ? checked
          ? (uncheckedValue as string)
          : (checkedValue as string)
        : !currentValue;
      input.onChange(nextValue);

      if (onValueChange) {
        onValueChange(nextValue, true);
      }
    });
  };

  const handleBlur = () => {
    input.onBlur(hasEnumMapping ? input.value : currentValue);
  };

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  const toggleExpanding = (
    event:
      | React.MouseEvent<HTMLSpanElement>
      | React.KeyboardEvent<HTMLSpanElement>
  ) => {
    event.stopPropagation();
    event.preventDefault();

    setExpanded(!expanded);
  };

  const getFormattedLabel = () => {
    return UtilService.htmlSubstring(
      getLabel(),
      getLabelLength(),
      !!withoutDotsAtExtensionPoint
    ).text;
  };

  const getFormattedLabelLength = () => {
    return UtilService.htmlSubstring(getLabel(), getLabelLength()).length;
  };

  const expandable = showAllLabel
    ? false
    : getFormattedLabelLength() >= getLabelLength() &&
      !label.endsWith(expandingPointMark);

  return {
    checked,
    error,
    expandable,
    expanded,
    getFormattedLabel,
    getLabel,
    handleChange,
    handleClick,
    handleBlur,
    toggleExpanding,
  };
}
